import { createApiClient } from "@trillion/ui";
import { getEnv } from "@trillion/ui/lib/env";
import type {
  EnqueuedJob,
  FailedJob,
  JobDetails,
  JobStats,
  JobTail,
  ProcessingJob,
  RecurringJob,
  RunHistoryEnvelope,
  ScheduledJob,
  SucceededJob,
} from "./jobs-types";
import type { CreateSettingsRequest, SettingView } from "./settings-types";

// Typed client over conduit-api's /admin/jobs/* surface (sc-86949). Lazy base
// (`() => getEnv(...)`) so the URL resolves per-request, after dev bootstrap
// populates window.__ENV__ — never captured empty at module load.
const api = createApiClient(() => getEnv("CONDUIT_API_URL"));

function page(from: number, count: number): string {
  return `from=${from}&count=${count}`;
}

// Every endpoint the SPA polls or triggers takes an optional AbortSignal, forwarded straight into
// `ApiClient.request`'s RequestInit (sc-100769). The shared client already accepts RequestInit and
// spreads it into `fetch`, so this needs no change to @trillion/ui. Cancellation is what makes an
// abandoned request actually SETTLE — without it a hung request never reaches its caller's `finally`,
// so the caller's in-flight guard, loading flag and row markers are all stranded, and each retry adds
// another genuinely in-flight request. [code review r3]
export const jobsApi = {
  stats: () => api.request<JobStats>("/admin/jobs/stats"),

  recurring: (signal?: AbortSignal) =>
    api.request<RecurringJob[]>("/admin/jobs/recurring", { signal }),

  enqueued: (queue = "default", from = 0, count = 20, signal?: AbortSignal) =>
    api.request<EnqueuedJob[]>(
      `/admin/jobs/enqueued?queue=${encodeURIComponent(queue)}&${page(from, count)}`,
      { signal }
    ),

  processing: (from = 0, count = 20, signal?: AbortSignal) =>
    api.request<ProcessingJob[]>(`/admin/jobs/processing?${page(from, count)}`, { signal }),

  // One recurring job's Processing runs (sc-114201, the job details page): the first 20 of that job's runs — the
  // server filters the whole Processing list before paging, so other jobs' runs never crowd them out.
  processingFor: (recurringJobId: string, signal?: AbortSignal) =>
    api.request<ProcessingJob[]>(
      `/admin/jobs/processing?${page(0, 20)}&recurringJobId=${encodeURIComponent(recurringJobId)}`,
      { signal }
    ),

  scheduled: (from = 0, count = 20, signal?: AbortSignal) =>
    api.request<ScheduledJob[]>(`/admin/jobs/scheduled?${page(from, count)}`, { signal }),

  // Run history (sc-114200): every run in the last `days` days, one request per grid load, as
  // `{ rows, truncated }`.
  succeeded: (days: number, signal?: AbortSignal) =>
    api.request<RunHistoryEnvelope<SucceededJob>>(`/admin/jobs/succeeded?days=${days}`, { signal }),

  failed: (days: number, signal?: AbortSignal) =>
    api.request<RunHistoryEnvelope<FailedJob>>(`/admin/jobs/failed?days=${days}`, { signal }),

  // One recurring job's run history (sc-114201, the job details page): the same window, narrowed server-side to
  // runs of the job type that definition invokes.
  succeededFor: (recurringJobId: string, days: number, signal?: AbortSignal) =>
    api.request<RunHistoryEnvelope<SucceededJob>>(
      `/admin/jobs/succeeded?days=${days}&recurringJobId=${encodeURIComponent(recurringJobId)}`,
      { signal }
    ),

  failedFor: (recurringJobId: string, days: number, signal?: AbortSignal) =>
    api.request<RunHistoryEnvelope<FailedJob>>(
      `/admin/jobs/failed?days=${days}&recurringJobId=${encodeURIComponent(recurringJobId)}`,
      { signal }
    ),

  details: (id: string) => api.request<JobDetails>(`/admin/jobs/${id}`),

  // Run-log tail (sc-114196): entries after `after`, current progress, and the `finished` stop signal. Its
  // own `admin-tail` rate-limit bucket server-side, so a 2 s poll never spends the grids' `admin` budget.
  tail: (id: string, after = 0, limit?: number, signal?: AbortSignal) =>
    api.request<JobTail>(
      `/admin/jobs/${encodeURIComponent(id)}/tail?after=${after}${limit === undefined ? "" : `&limit=${limit}`}`,
      { signal }
    ),

  trigger: (recurringJobId: string, signal?: AbortSignal) =>
    api.request<{ triggered: string }>("/admin/jobs/trigger", {
      method: "POST",
      body: JSON.stringify({ recurring_job_id: recurringJobId }),
      signal,
    }),

  requeue: (id: string) =>
    api.request<{ job_id: string; requeued: boolean }>(`/admin/jobs/${id}/requeue`, {
      method: "POST",
    }),

  remove: (id: string) =>
    api.request<{ job_id: string; deleted: boolean }>(`/admin/jobs/${id}`, {
      method: "DELETE",
    }),
};

// Typed client over conduit-api's /admin/settings/* surface (sc-94854 `SettingsController`).
// Reuses the same `api` instance (cookie auth via the default credentials:"include").
// scope/key path segments are URL-encoded — `scope` may carry a colon (job:<id>), which ASP.NET
// decodes back for {scope} binding; both are charset-bounded (no '/') by the create Zod schema.
export const settingsApi = {
  list: (scope?: string) =>
    api.request<SettingView[]>(
      `/admin/settings${scope ? `?scope=${encodeURIComponent(scope)}` : ""}`
    ),

  get: (scope: string, key: string) =>
    api.request<SettingView>(
      `/admin/settings/${encodeURIComponent(scope)}/${encodeURIComponent(key)}`
    ),

  create: (body: CreateSettingsRequest) =>
    api.request<SettingView>("/admin/settings", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  update: (scope: string, key: string, value: string) =>
    api.request<SettingView>(
      `/admin/settings/${encodeURIComponent(scope)}/${encodeURIComponent(key)}`,
      {
        method: "PUT",
        body: JSON.stringify({ value }),
      }
    ),

  // One-way plain → secret conversion (sc-116657): the server encrypts the value it already stores.
  // No body. An already-secret setting answers 200 with its masked view (idempotent); there is no
  // secret → plain counterpart.
  convertToSecret: (scope: string, key: string) =>
    api.request<SettingView>(
      `/admin/settings/${encodeURIComponent(scope)}/${encodeURIComponent(key)}/convert-to-secret`,
      {
        method: "POST",
      }
    ),

  remove: (scope: string, key: string) =>
    api.request<void>(
      `/admin/settings/${encodeURIComponent(scope)}/${encodeURIComponent(key)}`,
      {
        method: "DELETE",
      }
    ),
};
